/* Italian Secret, 2026-09-28: Andrea event delivery, standard B2B quotes and durable draft intake.
 * The server records campaign evidence and may fill an empty address after identity matching.
 * Server/n8n must validate, persist, deduplicate and acknowledge before success is shown.
 */
(() => {
  'use strict';
  const config = window.PIZZA_CONFIG;

  // Italian Secret, 2026-10-14 (PD-1309): one message per Core offer code.
  // Privacy: OFFER_ALREADY_REQUESTED is named only for the signed-in customer's own email; otherwise
  // the generic message neither confirms nor denies that an email was used before.
  const GENERIC_MESSAGE = 'The request could not be completed. Please try again.';
  function signedInEmail() {
    const field = document.querySelector('#sample-form input[name="email"][readonly]');
    const value = field ? String(field.getAttribute('value') || '').trim() : '';
    return /^[^@\s]+@[^@\s]+$/.test(value) ? value : '';
  }
  function maskEmail(email) {
    const [local, domain] = email.split('@');
    return local.slice(0, local.length > 2 ? 2 : 1) + '…@' + domain;
  }
  function ownRequest() {
    const account = signedInEmail();
    const field = document.querySelector('#sample-form input[name="email"]');
    return Boolean(account && field && field.value.trim().toLowerCase() === account.toLowerCase());
  }
  function offerMessage(code) {
    if (code === 'CUSTOMER_EMAIL_MISMATCH') {
      const account = signedInEmail();
      return { message: account
        ? `You’re signed in as ${maskEmail(account)}. Use that email address, or sign out to request samples with a different one.`
        : 'Please use the email address of your signed-in account, or sign out to use a different one.',
        signOutUrl: (config.cartRoot || '/').replace(/\/?$/, '/') + 'account/logout' };
    }
    if (code === 'OFFER_ALREADY_REQUESTED') {
      if (!ownRequest()) return { message: GENERIC_MESSAGE };
      window.dispatchEvent(new Event('italiansecret:offer-unavailable'));
      return { message: 'You’ve already requested this sample pack. Our team will call you to confirm your business and delivery details. Questions? Email info@italiansecret.co.uk.' };
    }
    if (code === 'OFFER_NOT_ELIGIBLE') {
      window.dispatchEvent(new Event('italiansecret:offer-unavailable'));
      return { message: 'This offer is reserved for invited businesses. To ask for an invitation, email info@italiansecret.co.uk.' };
    }
    return null;
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        // Italian Secret, 2026-10-14 (PD-1309): accept {error:"CODE"}, {error:{code}} and {code} shapes.
        const code = typeof failure.error === 'string' ? failure.error : (failure.error && typeof failure.error.code === 'string' ? failure.error.code : (typeof failure.code === 'string' ? failure.code : undefined));
        const coded = offerMessage(code);
        if (coded) { const error = new Error(coded.message); error.status = response.status; error.code = code; if (coded.signOutUrl) error.signOutUrl = coded.signOutUrl; throw error; }
        if (response.status === 409 && (!code || code === 'IDEMPOTENCY_CONFLICT')) {
          const conflict = new Error('Your details changed. Submit as a new request.');
          conflict.status = 409; conflict.code = 'IDEMPOTENCY_CONFLICT'; conflict.idempotencyConflict = true; throw conflict;
        }
        const error=new Error(code==='EXTRA_NOT_ALLOWED'||code==='INVALID_SAMPLE_PACK'?'Some selected products are no longer available. Remove extras or reload to choose another sample.':GENERIC_MESSAGE);error.status=response.status;error.code=code;throw error;
      }
      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('The service took too long to respond. Please retry; your request will not be duplicated.');
      throw error;
    } finally { clearTimeout(timer); }
  }
  function intakeUrl() {
    if (!config.submitUrl) throw new Error('Sample requests are not connected yet. Please contact info@italiansecret.co.uk.');
    const url = new URL(config.submitUrl, location.origin);
    if (url.username || url.password || (url.protocol !== 'https:' && url.origin !== location.origin)) {
      throw new Error('The sample request service is unavailable.');
    }
    return url;
  }

  // Italian Secret, 2026-10-14 (PD-1309): owner-configured n8n page-view webhooks.
  // Fire-and-forget only: never awaited by the journey, failures are swallowed, nothing is logged.
  let sectionSettings = {};
  try { sectionSettings = JSON.parse(document.getElementById('italiansecret-sample-config').textContent) || {}; } catch { sectionSettings = {}; }
  function n8nTargets(urls) {
    const targets = [];
    for (const value of urls) {
      try {
        if (typeof value !== 'string' || !value.trim()) continue;
        const url = new URL(value.trim(), location.href);
        if (url.protocol !== 'https:' || url.username || url.password) continue;
        if (!targets.includes(url.href)) targets.push(url.href);
      } catch { /* invalid setting: skip silently */ }
    }
    return targets;
  }
  function n8nPost(urls, fields) {
    const body = new URLSearchParams();
    Object.entries(fields).forEach(([key, value]) => { if (value !== undefined && value !== null && value !== '') body.set(key, typeof value === 'string' ? value : JSON.stringify(value)); });
    const encoded = body.toString();
    let dispatched = 0;
    for (const url of n8nTargets(urls)) {
      try {
        // no-cors + form encoding is a CORS-safelisted request: no preflight and no CORS console error.
        const pending = fetch(url, { method: 'POST', mode: 'no-cors', keepalive: true, credentials: 'omit',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }, body: encoded });
        if (pending && typeof pending.catch === 'function') pending.catch(() => {});
        dispatched++;
      } catch {
        try { if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([encoded], { type: 'application/x-www-form-urlencoded;charset=UTF-8' }))) dispatched++; } catch { /* silent */ }
      }
    }
    return dispatched;
  }
  function attribution() {
    const params = new URLSearchParams(location.search), utm = {};
    params.forEach((value, key) => { if (/^utm_[a-z0-9_]{1,40}$/i.test(key) && value) utm[key.toLowerCase()] = value.slice(0, 200); });
    return utm;
  }
  function sendViewOnce() {
    if (window.__italiansecretSampleViewSent) return; // one event per page load (bfcache restores do not re-run scripts)
    window.__italiansecretSampleViewSent = true;
    try {
      const params = new URLSearchParams(location.search);
      const customerId = (params.get('user') || '').trim().slice(0, 100);
      // Only the Liquid-rendered Shopify customer id (digits) of the signed-in visitor; never email/name/phone/address.
      const loggedIn = String(sectionSettings.loggedInCustomerId ?? '').trim();
      // Attribution ref from ?ref= / ?id= only. The #sample-invitation token is a credential and is never sent.
      const invitationRef = (params.get('ref') || params.get('id') || '').trim();
      const pageUrl = new URL(location.href); pageUrl.hash = '';
      n8nPost([sectionSettings.n8nViewUrl, sectionSettings.n8nViewUrl2], {
        event: 'sample_landing_viewed', event_id: (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(16).slice(2),
        customerId: customerId || null,
        logged_in_customer_id: /^\d{1,20}$/.test(loggedIn) ? loggedIn : null,
        invitation_ref: /^[A-Za-z0-9_.:-]{1,100}$/.test(invitationRef) ? invitationRef : null,
        page_url: pageUrl.href, timestamp: new Date().toISOString(), ...attribution()
      });
    } catch { /* never affect the page */ }
  }
  // Submission data reaches n8n only from Prometheus Core (sample_request_saved), never from the browser.
  window.PizzaN8n = { sendViewOnce };
  sendViewOnce();

  window.PizzaAPI = {
    async quote(extras,samples) {
      if (!config.quoteUrl) throw new Error('B2B prices are temporarily unavailable. Please try again.');
      const url = new URL(config.quoteUrl, location.origin);
      if (url.username || url.password || (url.protocol !== 'https:' && url.origin !== location.origin)) throw new Error('B2B pricing service unavailable.');
      const result = await request(url.href, {method:'POST', credentials:url.origin===location.origin?'same-origin':'omit',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({payload:JSON.stringify({campaignKey:config.campaignKey,...(config.managedCampaign&&/^[A-Za-z0-9_-]{32}$/.test(config.publicId||'')?{publicId:config.publicId}:{}),extras,samples})})});
      if(result.pricingPolicy!=='b2b_standard'||result.currency!==config.currency||!Array.isArray(result.lines)||!Array.isArray(result.catalog)||!Number.isSafeInteger(result.extrasSubtotalPence)||result.extrasSubtotalPence<0) throw new Error('B2B prices could not be confirmed.');
      return result;
    },
    async sendEvent(event) {
      if (!String(event.invitationRef || '').trim()) return { sent: false, reason: 'missing_user' };
      const deliveries = await Promise.allSettled(config.webhookUrls.map(endpoint => {
        const url = new URL(endpoint, location.href);
        if (url.protocol !== 'https:' && url.origin !== location.origin) throw new Error('Webhook must use HTTPS.');
        const body = new URLSearchParams({
          user: event.invitationRef, event: event.event, event_id: event.eventId,
          visit_id: event.visitId, sequence: String(event.sequence), occurred_at: event.occurredAt
        });
        if (event.step) body.set('step', String(event.step));
        if (event.stepName) body.set('step_name', event.stepName);
        if (event.requestId) body.set('request_id', event.requestId);
        return fetch(url, { method: 'POST', mode: 'no-cors', keepalive: true, credentials: 'omit',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }, body: body.toString() });
      }));
      // An opaque response proves only dispatch, never delivery or CRM persistence.
      return { dispatched: deliveries.some(result => result.status === 'fulfilled'), deliveries: deliveries.length };
    },
    async lookupCustomer(ref) {
      if (config.previewMode && ref === 'demo-client') return { id: 'SAMPLE-1042', name: 'Alex Morgan', business: 'The Olive Tree', email: 'alex@example.com', phone: '+447700900123' };
      // Italian Secret, 2026-10-12: user/ref is attribution; only the private invitation exchange permits prefill.
      return config.managedCampaign && window.PizzaInvitation?.present
        ? window.PizzaInvitation.resolve(config.publicId) : null;
    },
    async getAvailability() { return null; }, // Preserve Andrea's approved date-seeded counter.
    async submitInterest(payload) {
      if (config.previewMode) return { requestId: payload.requestId, status: 'awaiting_call' };
      const url = intakeUrl();
      // Added by Italian Secret on 2026-10-08: Managed Shopify proxy accepts JSON; legacy webhooks retain form encoding.
      const body = { request_id: payload.requestId, payload: JSON.stringify({
        ...payload, schemaVersion: 2, campaignKey: config.campaignKey, ...(config.managedCampaign&&/^[A-Za-z0-9_-]{32}$/.test(config.publicId||'')?{publicId:config.publicId}:{}),
        source: 'italiansecret_sample_landing', shopDomain: config.shopDomain,
        crmWriteMode: 'fill_empty_address', updateCanonicalProfile: false
      }) };
      const result = await request(url.href, { method: 'POST', credentials: url.origin === location.origin ? 'same-origin' : 'omit',
        headers: { 'Content-Type': config.managedCampaign ? 'application/json' : 'application/x-www-form-urlencoded;charset=UTF-8' }, body: config.managedCampaign ? JSON.stringify(body) : new URLSearchParams(body).toString() });
      if (result.requestId !== payload.requestId || result.status !== (payload.extras.length?'checkout_ready':'awaiting_call') || result.persisted !== true) {
        throw new Error('We could not confirm that your request was saved. Please try again.');
      }
      if (!result.draftOrderId || !/^gid:\/\/shopify\/DraftOrder\/\d+$/.test(result.draftOrderId)) {
        throw new Error('Your request could not be confirmed completely. Please retry so we can confirm your pack.');
      }
      if(payload.extras.length){
        const checkout=new URL(result.checkoutUrl||'');
        if(checkout.protocol!=='https:'||![config.shopDomain,location.hostname].includes(checkout.hostname)||checkout.username||checkout.password||!/^\/(?:\d+\/)?(?:invoices|checkouts)\//.test(checkout.pathname))throw new Error('The secure checkout could not be confirmed. Please retry.');
      }
      return result;
    }
  };
})();
